import type { TFunction } from 'i18next'

import { ApiError, type ErrorSubject } from '../../lib/api'
import {
  GENERIC_ERROR,
  messageFor,
  toFormErrors,
  withoutErrorsFor,
  type FormErrors,
} from '../../lib/formErrors'

/**
 * Which box on the load form each rejection belongs to. The two shared value-object
 * codes (weight, pallets) keep one field each on this form, which is where "one code,
 * one field" has to hold; the list-level stop codes land on the stops as a whole.
 */
const FIELD_BY_CODE: Record<string, string> = {
  load_source_required: 'source',
  // Nobody named and no company typed: the company's box is the one on screen then (TRKC-104).
  load_broker_required: 'brokerCompany',
  load_company_too_long: 'brokerCompany',
  load_broker_inactive: 'brokerId',
  load_agent_not_found: 'agentId',
  load_agent_other_broker: 'agentId',
  load_agent_inactive: 'agentId',
  load_equipment_required: 'equipment',
  load_alternative_equipment_invalid: 'alternativeEquipment',
  load_dat_equipment_code_invalid: 'datEquipmentCode',
  load_load_type_required: 'loadType',
  load_weight_required: 'weight',
  weight_out_of_range: 'weight',
  load_length_invalid: 'lengthFeet',
  pallet_capacity_out_of_range: 'pallets',
  load_trip_miles_invalid: 'tripMiles',
  load_reefer_range_invalid: 'reeferMin',
  load_reefer_range_without_reefer: 'reeferMin',
  load_reefer_range_required: 'reeferMin',
  load_features_invalid: 'features',
  load_feature_not_for_equipment: 'features',
  load_securement_invalid: 'securement',
  load_securement_without_open_deck: 'securement',
  load_rate_invalid: 'rate',
  money_too_large: 'rate',
  load_commodity_too_long: 'commodity',
  load_dat_reference_too_long: 'datReference',
  load_notes_too_long: 'notes',
  load_stops_pickup_required: 'stops',
  load_stops_delivery_required: 'stops',
  load_stops_order: 'stops',
  load_stops_too_many: 'stops',
}

/**
 * A stop's complaint carries its position (`load_stop_2_city_required`), because the stops
 * are a list and each stop's box is its own field — the insurance certificate's pattern.
 * The rule names the box; all four date rules land on the stop's first day.
 */
const STOP_CODE = /^load_stop_(\d+)_(.+)$/

const STOP_FIELD_BY_RULE: Record<string, string> = {
  type_invalid: 'type',
  city_required: 'city',
  city_too_long: 'city',
  state_invalid: 'state',
  company_too_long: 'company',
  street_too_long: 'street',
  date_required: 'windowFrom',
  window_inverted: 'windowFrom',
  window_past: 'windowFrom',
  before_previous: 'windowFrom',
  reference_too_long: 'referenceNumber',
  notes_too_long: 'notes',
}

/** The form's name for one stop's box: `stops.1.city` for the second stop's city. */
export const stopField = (index: number, name: string): string => `stops.${index}.${name}`

function stopFieldOf(code: string): string | undefined {
  const match = STOP_CODE.exec(code)
  const field = match === null ? undefined : STOP_FIELD_BY_RULE[match[2]!]
  return match === null || field === undefined ? undefined : stopField(Number(match[1]) - 1, field)
}

/**
 * Complaints that more than one box can fix: a range is mended from either temperature,
 * a reefer range also by naming a reefer, and a contact by naming another broker.
 */
const CLEARED_BY: Record<string, readonly string[]> = {
  load_reefer_range_invalid: ['reeferMin', 'reeferMax'],
  load_reefer_range_without_reefer: ['reeferMin', 'reeferMax', 'equipment', 'alternativeEquipment'],
  load_reefer_range_required: ['reeferMin', 'reeferMax', 'equipment'],
  load_alternative_equipment_invalid: ['alternativeEquipment', 'equipment'],
  load_feature_not_for_equipment: ['features', 'equipment', 'alternativeEquipment'],
  load_securement_without_open_deck: ['securement', 'equipment', 'alternativeEquipment'],
  load_agent_other_broker: ['agentId', 'brokerId'],
}

export { GENERIC_ERROR }
export type { FormErrors }

export function toLoadFormErrors(caught: unknown): FormErrors {
  const stopFields = Object.fromEntries(
    (caught instanceof ApiError ? caught.codes : []).flatMap((code) => {
      const field = stopFieldOf(code)
      return field === undefined ? [] : [[code, field] as const]
    }),
  )

  return toFormErrors(caught, { ...FIELD_BY_CODE, ...stopFields })
}

export function withoutLoadErrorsFor(errors: FormErrors, editedField: string): FormErrors {
  // A stop's window is one complaint on its first day, mended from either end.
  const windowEdit = /^stops\.(\d+)\.windowTo$/.exec(editedField)
  const cleared = withoutErrorsFor(errors, editedField, CLEARED_BY)

  return windowEdit === null ? cleared : withoutErrorsFor(cleared, stopField(Number(windowEdit[1]), 'windowFrom'))
}

/**
 * The sentence for one code. A stop's code is translated from its rule — one sentence
 * per rule, the stop's position interpolated — so ten positions need no ten keys each.
 */
export function loadMessageFor(code: string, t: TFunction, subject?: ErrorSubject): string {
  const match = STOP_CODE.exec(code)

  if (match !== null) {
    const key = `errors.load_stop_${match[2]!}`
    const translated = t(key, { position: Number(match[1]) })
    return translated === key ? t('loads.form.saveError') : translated
  }

  return messageFor(code, t, 'loads.form.saveError', subject)
}
